import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Clock, MapPin, ChevronLeft, ChevronRight, List, LayoutGrid, X, Trophy, GraduationCap, CalendarDays } from 'lucide-react';
import { academyAPI } from '../../services/api';
import { btn } from '../../utils/buttonStyles';

/**
 * Everything the academy has scheduled, in one week view.
 *
 * An academy owns no events of its own — its schedule is the union of what its
 * programs' squads and its tournaments have booked, which the API merges. This
 * shows real dated events for a chosen week rather than a repeating template,
 * so navigating weeks is meaningful and "nothing scheduled" is a real answer.
 */

// ─── Config ───────────────────────────────────────────────────────────────────
// Tall enough that a quarter-hour block is still legible at its true height.
// At 64 it was 16px, so short games were being padded up to a minimum and a
// 15-minute match drew the same as a 30-minute one — the grid contradicted the
// time printed inside it.
const HOUR_PX = 88;
const PX_PER_MIN = HOUR_PX / 60;

// Only rescues a degenerate near-zero event from being unclickable; at this
// size it distorts nothing a reader could measure. Anything longer than about
// nine minutes draws exactly to scale.
const MIN_BLOCK_PX = 14;
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_SHORT = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

// Padding around the day's real extent, so a block never sits flush against the edge.
const HOUR_PAD = 1;
const DEFAULT_START = 6;
const DEFAULT_END = 21;

const PALETTES = [
  { bg: '#dbeafe', border: '#3b82f6', text: '#1d4ed8', dark: '#1e40af' },
  { bg: '#d1fae5', border: '#10b981', text: '#047857', dark: '#065f46' },
  { bg: '#ede9fe', border: '#8b5cf6', text: '#6d28d9', dark: '#4c1d95' },
  { bg: '#fce7f3', border: '#ec4899', text: '#be185d', dark: '#9d174d' },
  { bg: '#fef3c7', border: '#f59e0b', text: '#b45309', dark: '#78350f' },
  { bg: '#fee2e2', border: '#ef4444', text: '#b91c1c', dark: '#7f1d1d' },
];

// ─── Date helpers ─────────────────────────────────────────────────────────────
/** Monday of the week containing `d`, at local midnight. */
function startOfWeek(d) {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  const dow = out.getDay();                 // 0 = Sunday
  out.setDate(out.getDate() - (dow === 0 ? 6 : dow - 1));
  return out;
}
const addDays = (d, n) => { const o = new Date(d); o.setDate(o.getDate() + n); return o; };
/** Local YYYY-MM-DD. toISOString would shift the day for anyone behind UTC. */
const localDate = (d) => d.toLocaleDateString('en-CA');
const sameDay = (a, b) => localDate(a) === localDate(b);
const minutesInto = (d) => d.getHours() * 60 + d.getMinutes();
const formatClock = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * Lay overlapping blocks side by side.
 *
 * Two sessions at the same hour would otherwise cover each other, and the one
 * underneath is invisible rather than merely cramped. Each block gets the
 * left-most lane free at its start time.
 *
 * Width is decided per run of overlapping blocks, not per day. Counting lanes
 * across the whole column meant one busy evening squeezed every block that day
 * to a third of the width — including a morning class with nothing beside it,
 * which then looked like it was sharing a court it had to itself.
 */
function assignLanes(blocks) {
  const ordered = [...blocks].sort((a, b) => a.start - b.start || a.end - b.end);

  const out = [];
  let group = [];
  let groupEnd = null;   // latest end within the current run, as a timestamp

  const closeGroup = () => {
    if (!group.length) return;
    const laneEnds = [];
    group.forEach((b) => {
      let lane = laneEnds.findIndex(end => end <= +b.start);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
      laneEnds[lane] = +b.end;
      b.lane = lane;
    });
    const lanes = Math.max(laneEnds.length, 1);
    group.forEach((b) => { b.lanes = lanes; });
    out.push(...group);
    group = [];
    groupEnd = null;
  };

  ordered.forEach((b) => {
    // A block starting at or after everything before it has ended shares the
    // column with none of them, so it begins a fresh run and gets full width.
    if (groupEnd !== null && +b.start >= groupEnd) closeGroup();
    group.push(b);
    groupEnd = groupEnd === null ? +b.end : Math.max(groupEnd, +b.end);
  });
  closeGroup();

  return out;
}

// ─── Blocks ───────────────────────────────────────────────────────────────────
function SessionBlock({ block, isSelected, onClick, hourStart }) {
  const { palette, start, end, item, lane, lanes } = block;
  const top = (minutesInto(start) - hourStart * 60) * PX_PER_MIN;
  const height = Math.max(((end - start) / 60000) * PX_PER_MIN, MIN_BLOCK_PX);
  const widthPct = 100 / lanes;

  return (
    <div
      onClick={() => onClick(block)}
      title={`${item.sourceName || item.subject} — ${formatClock(start)}`}
      style={{
        position: 'absolute',
        top,
        height,
        left: `calc(${lane * widthPct}% + 2px)`,
        width: `calc(${widthPct}% - 4px)`,
        backgroundColor: palette.bg,
        borderLeft: `3px solid ${palette.border}`,
        // A tournament window is the container its fixtures sit inside, so it
        // is outlined rather than drawn as another solid booking.
        border: item.kind === 'tournament' ? `1px dashed ${palette.border}` : undefined,
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: palette.border,
        borderRadius: 6,
        cursor: 'pointer',
        boxShadow: isSelected
          ? `0 0 0 2px ${palette.border}, 0 4px 12px rgba(0,0,0,0.15)`
          : '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'box-shadow .15s, transform .15s',
        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
        overflow: 'hidden',
        zIndex: isSelected ? 10 : 1,
        display: 'flex',
        flexDirection: 'column',
        padding: height >= 44 ? '4px 6px' : '2px 5px',
      }}
    >
      <p style={{ fontSize: 11, fontWeight: 700, color: palette.text, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {item.kind === 'tournament' ? '🏆 ' : ''}{item.subject}
      </p>
      {height >= 44 && (
        <p style={{ fontSize: 10, color: palette.dark, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {block.continued ? '↑ ' : ''}{formatClock(start)} · {Math.round((end - start) / 60000)}m{block.continues ? ' →' : ''}
        </p>
      )}
      {height >= 60 && item.sourceName && (
        <p style={{ fontSize: 10, color: palette.dark, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.sourceName}
        </p>
      )}
    </div>
  );
}

function SourceBadge({ source }) {
  const isTournament = source === 'tournament';
  const Icon = isTournament ? Trophy : GraduationCap;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded"
      style={{
        backgroundColor: isTournament ? 'rgba(245,158,11,.15)' : 'rgba(59,130,246,.15)',
        color: isTournament ? '#b45309' : '#1d4ed8',
      }}
    >
      <Icon size={10} /> {isTournament ? 'Tournament' : 'Class'}
    </span>
  );
}

function DetailCard({ block, theme, onClose }) {
  // The block may be one day's slice of a longer item; report the real span.
  const { palette, item } = block;
  const start = block.fullStart || block.start;
  const end = block.fullEnd || block.end;
  const spansDays = !sameDay(start, end);
  return (
    <div className={`${theme.card} rounded-xl border-2 p-5 relative`} style={{ borderColor: palette.border }}>
      <button onClick={onClose} aria-label="Close" className={`absolute top-3 right-3 ${btn.iconSm} ${theme.cardHover}`}>
        <X size={15} className={theme.textMuted} />
      </button>

      <div className="flex items-start gap-3 mb-4">
        <div className="w-1 h-12 rounded-full shrink-0" style={{ backgroundColor: palette.border }} />
        <div className="min-w-0 pr-6">
          <div className="flex items-center gap-2 mb-0.5">
            <SourceBadge source={item.source} />
          </div>
          <h3 className={`text-lg font-bold ${theme.text}`}>{item.subject}</h3>
          {item.sourceName && <p className={`text-sm ${theme.textSecondary}`}>{item.sourceName}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays size={14} className={theme.textMuted} />
          <div>
            <p className={`text-xs ${theme.textMuted}`}>Date</p>
            <p className={`text-sm font-medium ${theme.text}`}>
              {start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
              {spansDays && ` – ${end.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock size={14} className={theme.textMuted} />
          <div>
            <p className={`text-xs ${theme.textMuted}`}>Time</p>
            <p className={`text-sm font-medium ${theme.text}`}>{formatClock(start)} – {formatClock(end)}</p>
          </div>
        </div>
        {item.squadName && (
          <div className="flex items-center gap-2">
            <GraduationCap size={14} className={theme.textMuted} />
            <div>
              <p className={`text-xs ${theme.textMuted}`}>Squad</p>
              <p className={`text-sm font-medium ${theme.text}`}>{item.squadName}</p>
            </div>
          </div>
        )}
        {item.venueName && (
          <div className="flex items-center gap-2">
            <MapPin size={14} className={theme.textMuted} />
            <div className="min-w-0">
              <p className={`text-xs ${theme.textMuted}`}>Venue</p>
              <p className={`text-sm font-medium ${theme.text} truncate`}>{item.venueName}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ListView({ blocks, days, theme, onSelect }) {
  const today = new Date();
  const anything = blocks.length > 0;
  if (!anything) return null;

  return (
    <div className="space-y-6">
      {days.map((day, di) => {
        const dayBlocks = blocks.filter(b => sameDay(b.start, day)).sort((a, b) => a.start - b.start);
        if (!dayBlocks.length) return null;
        const isToday = sameDay(day, today);
        return (
          <div key={localDate(day)}>
            <h3 className={`text-sm font-bold ${theme.textSecondary} uppercase tracking-wider mb-3 flex items-center gap-2`}>
              <span className={`w-2 h-2 rounded-full inline-block ${isToday ? 'bg-blue-500' : 'bg-gray-400'}`} />
              {DAY_LABELS[di]} {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {isToday && <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-semibold normal-case tracking-normal">Today</span>}
            </h3>
            <div className="space-y-2">
              {dayBlocks.map(block => (
                <div
                  key={`${block.item.id}-${block.dayIndex}`}
                  onClick={() => onSelect(block)}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${theme.border} ${theme.cardHover} transition cursor-pointer`}
                >
                  <div className="w-1 h-10 rounded-full shrink-0" style={{ backgroundColor: block.palette.border }} />
                  <div className="w-24 shrink-0">
                    <p className={`text-sm font-semibold ${theme.text}`}>{formatClock(block.start)}</p>
                    <p className={`text-xs ${theme.textMuted}`}>{Math.round((block.end - block.start) / 60000)}m</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${theme.text} truncate`}>{block.item.subject}</p>
                    <p className={`text-xs ${theme.textSecondary} truncate`}>
                      {[block.item.sourceName, block.item.squadName, block.item.venueName].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <SourceBadge source={block.item.source} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const AcademyTimetable = ({ academyId }) => {
  const { theme } = useTheme();
  const [viewMode, setViewMode] = useState('grid');
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const load = useCallback(async () => {
    if (!academyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await academyAPI.schedule(academyId, {
        startDate: localDate(weekStart),
        endDate:   localDate(addDays(weekStart, 6)),
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Could not load the schedule');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [academyId, weekStart]);

  useEffect(() => { load(); }, [load]);
  // A block from the previous week must not stay open over the new one.
  useEffect(() => { setSelected(null); }, [weekStart]);

  /**
   * One colour per owning entity, assigned in first-seen order and held in a
   * map so a programme keeps its colour as the week changes. Colour follows
   * the entity, never its position in the list.
   */
  const palettesBySource = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      const key = `${it.source}:${it.sourceId ?? it.subject}`;
      if (!map.has(key)) map.set(key, PALETTES[map.size % PALETTES.length]);
    });
    return map;
  }, [items]);

  const blocks = useMemo(() => {
    // An item is cut into one segment per day it covers. A tournament runs
    // across days, and so can a session crossing midnight; keyed only to its
    // start day, such an item would draw one column-height block that ran off
    // the bottom and left the days it actually occupies looking empty.
    const segments = [];
    items.forEach((it) => {
      const start = new Date(it.startTime);
      const rawEnd = new Date(it.endTime);
      const end = rawEnd > start ? rawEnd : new Date(start.getTime() + 30 * 60000);
      const palette = palettesBySource.get(`${it.source}:${it.sourceId ?? it.subject}`) || PALETTES[0];

      days.forEach((day, dayIndex) => {
        const dayStart = new Date(day);
        const dayEnd = addDays(dayStart, 1);
        if (end <= dayStart || start >= dayEnd) return;
        segments.push({
          item: it,
          palette,
          dayIndex,
          // Clamped to the day so the block stays inside its column; the
          // detail card still reports the item's true start and end.
          start: start > dayStart ? start : dayStart,
          end: end < dayEnd ? end : new Date(dayEnd.getTime() - 60000),
          fullStart: start,
          fullEnd: end,
          continues: end > dayEnd,
          continued: start < dayStart,
        });
      });
    });
    // Lanes are per day — overlap only matters within a column.
    return days.flatMap((_, i) => assignLanes(segments.filter(s => s.dayIndex === i)));
  }, [items, palettesBySource, days]);

  // The grid spans the events actually present, so an academy that only trains
  // in the evening is not scrolled past fifteen empty morning hours.
  const [hourStart, hourEnd] = useMemo(() => {
    if (!blocks.length) return [DEFAULT_START, DEFAULT_END];
    const starts = blocks.map(b => minutesInto(b.start) / 60);
    const ends = blocks.map(b => minutesInto(b.end) / 60 || 24);
    return [
      Math.max(0, Math.floor(Math.min(...starts)) - HOUR_PAD),
      Math.min(24, Math.ceil(Math.max(...ends)) + HOUR_PAD),
    ];
  }, [blocks]);

  const hourRows = Array.from({ length: Math.max(hourEnd - hourStart, 1) }, (_, i) => hourStart + i);
  const totalHeight = hourRows.length * HOUR_PX;

  const legend = useMemo(() => {
    const seen = new Map();
    items.forEach((it) => {
      const key = `${it.source}:${it.sourceId ?? it.subject}`;
      if (!seen.has(key)) {
        seen.set(key, { key, name: it.sourceName || it.subject, source: it.source, palette: palettesBySource.get(key) });
      }
    });
    return [...seen.values()];
  }, [items, palettesBySource]);

  const visibleDays = selectedDay ? days.filter(d => localDate(d) === selectedDay) : days;
  const today = new Date();
  const weekLabel = `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${addDays(weekStart, 6).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

  const handleSelect = (block) =>
    setSelected(prev => (prev?.item.id === block.item.id ? null : block));

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text}`}>Weekly Timetable</h2>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            Class sessions and tournament fixtures across the academy
          </p>
        </div>

        <div className={`flex rounded-lg border ${theme.border} overflow-hidden`}>
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm transition ${viewMode === 'grid' ? theme.tabActive : theme.tabInactive}`}
          >
            <LayoutGrid size={14} /> Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm transition ${viewMode === 'list' ? theme.tabActive : theme.tabInactive}`}
          >
            <List size={14} /> List
          </button>
        </div>
      </div>

      {/* Week navigation */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setWeekStart(prev => addDays(prev, -7))}
          aria-label="Previous week"
          className={`${btn.icon} border ${theme.border} ${theme.cardHover} ${theme.text}`}
        >
          <ChevronLeft size={16} />
        </button>
        <button
          onClick={() => setWeekStart(prev => addDays(prev, 7))}
          aria-label="Next week"
          className={`${btn.icon} border ${theme.border} ${theme.cardHover} ${theme.text}`}
        >
          <ChevronRight size={16} />
        </button>
        <span className={`text-sm font-semibold ${theme.text}`}>{weekLabel}</span>
        <button
          onClick={() => setWeekStart(startOfWeek(new Date()))}
          className={`${btn.base} ${btn.sm} border ${theme.border} ${theme.text} ${theme.cardHover}`}
        >
          This week
        </button>
        {loading && <span className={`text-xs ${theme.textMuted}`}>Loading…</span>}
      </div>

      {/* Legend — one entry per owning program or tournament */}
      {legend.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5">
          {legend.map(l => (
            <div key={l.key} className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: l.palette.border }} />
              <span className={`text-xs ${theme.textSecondary}`}>{l.name}</span>
              {l.source === 'tournament' && <Trophy size={11} className={theme.textMuted} />}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-400/50 text-sm text-red-500">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 ? (
        <div className={`rounded-xl border ${theme.border} p-10 text-center`}>
          <CalendarDays size={28} className={`mx-auto mb-3 ${theme.textMuted}`} />
          <p className={`text-sm font-medium ${theme.text}`}>Nothing scheduled this week</p>
          <p className={`text-xs ${theme.textSecondary} mt-1`}>
            Sessions booked by program squads and academy tournaments appear here.
          </p>
        </div>
      ) : viewMode === 'list' ? (
        <ListView blocks={blocks} days={days} theme={theme} onSelect={handleSelect} />
      ) : (
        <>
          {/* Day filter pills */}
          <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
            <button
              onClick={() => setSelectedDay(null)}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition border ${
                !selectedDay ? 'bg-blue-600 text-white border-blue-600' : `${theme.border} ${theme.text} ${theme.cardHover}`
              }`}
            >
              All Days
            </button>
            {days.map((d, i) => {
              const key = localDate(d);
              const active = selectedDay === key;
              return (
                <button
                  key={key}
                  onClick={() => setSelectedDay(active ? null : key)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition border ${
                    active ? 'bg-blue-600 text-white border-blue-600' : `${theme.border} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  {DAY_SHORT[i].charAt(0) + DAY_SHORT[i].slice(1, 3).toLowerCase()} {d.getDate()}
                  {sameDay(d, today) && ' ·'}
                </button>
              );
            })}
          </div>

          {/* Grid */}
          <div className={`rounded-xl border ${theme.border} overflow-x-auto`}>
            <div style={{ minWidth: visibleDays.length > 1 ? 720 : 320 }}>
              {/* Column headers */}
              <div className="flex border-b" style={{ borderColor: 'rgba(128,128,128,.3)' }}>
                <div className="w-14 shrink-0" />
                {visibleDays.map((d, i) => {
                  const isToday = sameDay(d, today);
                  return (
                    <div key={localDate(d)} className="flex-1 text-center py-2">
                      <p className={`text-xs font-bold ${isToday ? 'text-blue-500' : theme.textSecondary}`}>
                        {DAY_SHORT[days.indexOf(d)] || DAY_SHORT[i]}
                      </p>
                      <p className={`text-xs ${isToday ? 'text-blue-500 font-semibold' : theme.textMuted}`}>
                        {d.getDate()}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Hour rows + blocks */}
              <div className="flex" style={{ height: totalHeight, position: 'relative' }}>
                <div className="w-14 shrink-0 relative">
                  {hourRows.map((h, i) => (
                    <div
                      key={h}
                      className={`text-[10px] ${theme.textMuted} text-right pr-2`}
                      style={{ position: 'absolute', top: i * HOUR_PX - 5, right: 0 }}
                    >
                      {h % 12 || 12}{h >= 12 ? 'pm' : 'am'}
                    </div>
                  ))}
                </div>

                {visibleDays.map((d) => (
                  <div key={localDate(d)} className="flex-1 relative" style={{ borderLeft: '1px solid rgba(128,128,128,.18)' }}>
                    {hourRows.map((h, i) => (
                      <div
                        key={h}
                        style={{
                          position: 'absolute', top: i * HOUR_PX, left: 0, right: 0,
                          borderTop: '1px solid rgba(128,128,128,.12)',
                        }}
                      />
                    ))}
                    {blocks.filter(b => sameDay(b.start, d)).map(block => (
                      <SessionBlock
                        key={`${block.item.id}-${block.dayIndex}`}
                        block={block}
                        hourStart={hourStart}
                        isSelected={selected?.item.id === block.item.id}
                        onClick={handleSelect}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {selected && (
        <div className="mt-5 max-w-md">
          <DetailCard block={selected} theme={theme} onClose={() => setSelected(null)} />
        </div>
      )}
    </div>
  );
};

export default AcademyTimetable;
